import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, { useEffect, useState, useCallback, useContext } from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { apiGet, apiPost, apiPut } from "helpers/Request";
import {
    transform as assignmentTransformer,
    AssignmentObjectSchema,
    prepare as assignmentPreparer,
} from "schemas/Assignment";
import EditForm from "./EditForm";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AssignmentContext } from "context/Assignment";
import { AppContext } from "context/App";
import { toast } from "react-hot-toast";
import { useAsyncError } from "hooks/AsyncError";
import AssignmentComments from "./Comments";
import {
    fromString,
    fromLocalString,
    HTML_DATE_FORMAT,
    SQL_DATE_TIME_FORMAT,
} from "helpers/Date";

function AssignmentEditScreen(props) {
    const { uuid } = useParams();
    const location = useLocation();
    const auth = useAuth();
    const dispatch = useDispatch();
    const history = useHistory();
    const throwError = useAsyncError();
    const [assignment, setAssignment] = useState(AssignmentObjectSchema);
    const [formValues, setFormValues] = useState(AssignmentObjectSchema);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isUserEditable, setIsUserEditable] = useState(false);
    const [preformingStateUpdate, setPreformingStateUpdate] = useState(false);
    const [state, setState] = useState(0);
    const { setCurrentNav } = useContext(AppContext);

    const contentFlow = useSelector((state) => state.contentFlow);

    const handleRequestReload = () => {
        setState((state) => state + 1);
    };

    const ensureAssignmentShape = useCallback(
        (assignment) => {
            return Object.assign({}, assignment, {
                users: assignment.users.map((user) => {
                    return { label: user.name, value: user.uuid };
                }),
                workflow: assignment.workflow_id
                    ? {
                          value: assignment.workflow.id,
                          label: assignment.workflow.name,
                      }
                    : {
                          value: contentFlow.workflows[0].id,
                          label: contentFlow.workflows[0].name,
                      },
                children: assignment.children.map((content) => {
                    return { uuid: content.uuid };
                }),
                issue_label: assignment.issue?.decorated_label || null,
                completed: assignment.id ? !assignment.status : 0,
                due_at: assignment.due_at
                    ? fromString(assignment.due_at)
                          .toLocal()
                          .toFormat(HTML_DATE_FORMAT)
                    : null,
            });
        },
        [contentFlow]
    );

    const onSubmit = (values) => {
        if (assignment.lock && assignment.lock.user.uuid !== auth.user.uuid) {
            console.log("not locked by user");
            return;
        } else if (assignment.uuid && !assignment.lock) {
            console.log("no content lock");
            return;
        }

        const users = values.users.map((user) => user.value);
        const children = values.children.map((content) => content.uuid);

        const data = assignmentPreparer(
            Object.assign({}, assignment, values, {
                users: users,
                workflow_id: values.workflow.value,
                children: children,
                status: !values.completed,
                action_start_time: values.action_start_time
                    ? fromString(values.action_start_time)
                          .toUTC()
                          .toFormat(SQL_DATE_TIME_FORMAT)
                    : null,
                action_end_time: values.action_end_time
                    ? fromString(values.action_end_time)
                          .toUTC()
                          .toFormat(SQL_DATE_TIME_FORMAT)
                    : null,
                due_at: values.due_at
                    ? fromLocalString(values.due_at, HTML_DATE_FORMAT)
                          .toUTC()
                          .toFormat(SQL_DATE_TIME_FORMAT)
                    : null,
                issue_label: undefined,
                completed: undefined,
            })
        );

        setSaving(true);
        const toastId = toast("Saving...");

        const apiAction = assignment.uuid ? apiPut : apiPost;

        return apiAction(
            "/v4/assignment/" +
                (assignment.uuid ? assignment.uuid : "") +
                "?include=users,children",
            auth.publication,
            data
        )
            .then((resp) => {
                const item = assignmentTransformer(resp);
                toast.success("Assignment saved", { id: toastId });

                if (!assignment.uuid) {
                    // was a new item, so redirect
                    apiPost("/v4/lock-request", auth.publication, {
                        srn: item.srn,
                    })
                        .then((resp) => {
                            history.push(item.edit_url);
                        })
                        .catch((err) => {
                            toast.error("Unable to acquire lock", {
                                id: toastId,
                            });
                        });
                }

                setAssignment(item);
                setSaving(false);
                setTimeout(() => {
                    setLoading(false);
                }, 0);

                // return releaseLockOnSave;
            })
            .catch((err) => {
                setSaving(false);
                toast.error("Unable to save your changes, please try again.");
            });
    };

    const onRequestNavigate = (url) => {
        history.push(url);
    };

    useEffect(() => {
        setLoading(true);
        setAssignment(AssignmentObjectSchema);
        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setLoading(false);
            }, 0);
            return;
        }

        apiGet("/v4/assignment/" + uuid, auth.publication, {
            include: "users,children",
        })
            .then((response) => {
                const item = assignmentTransformer(response);

                setAssignment(item);
                // dispatch(addRecentItem(item));
                setTimeout(() => {
                    setLoading(false);
                }, 0);
            })
            .catch((err) => {
                if (err.message === "failed refresh") {
                    throwError(new Error("Failed to refresh auth token"));
                    return;
                }
                throwError(new Error("Failed to locate content."));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication, dispatch, state]);

    useEffect(() => {
        setCurrentNav("planner");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setFormValues(ensureAssignmentShape(assignment));
        });
    }, [assignment, ensureAssignmentShape]);

    useEffect(() => {
        if (
            assignment.uuid &&
            assignment.lock &&
            assignment.lock.user.uuid === auth.user.uuid
        ) {
            setIsUserEditable(true);
        } else if (!assignment.uuid) {
            setIsUserEditable(true);
        } else {
            setIsUserEditable(false);
        }
    }, [assignment, auth.user]);

    // content and form values are provided via context provider
    return (
        <AssignmentContext.Provider
            value={{
                assignment: assignment,
                loading: loading,
                formValues: formValues,
                requestReload: handleRequestReload,
                preformingStateUpdate,
                setPreformingStateUpdate,
                saving,
                setSaving,
                isUserEditable,
            }}
        >
            <MainBlock className="z-0">
                <EditForm
                    onSubmit={onSubmit}
                    onRequestNavigate={onRequestNavigate}
                />
                <AssignmentComments />
            </MainBlock>
        </AssignmentContext.Provider>
    );
}

export default AssignmentEditScreen;
